import { Asset, EntryCollection } from 'contentful';
import { IAboutFields, IExperienceFields, IBlogPostFields } from '../../@types/generated/contentful';
import { ThumbNail } from '../components/uiParts/ThumbNails/ThumbNailsMini/ThumbNailsMini';

type AssetResources = Record<string, string>;

interface TransformedProjectFields {
  title: string;
  /** Slug */
  slug: string;
  /** Thumbnail */
  thumbnail: any;
  /** PC Capture */
  pcCapture?: any;
  /** SP Capture */
  spCapture?: any;
  /** client */
  client?: string;
  /** Description */
  description: string;
  /** Captures */
  captures?: string[];
  /** Publish Date */
  publishDate: string;
  /** Tags */
  tags?: ('general' | 'javascript' | 'static-sites')[] | undefined;
}

export interface IBlogPostTransformed {
  id: string;
  resources: AssetResources;
  fields: TransformedProjectFields;
}

function getContentfulBaseUrl(): string {
  return `https://cdn.contentful.com/spaces/${process.env.REACT_APP_SPACE_ID}/environments/${process.env.REACT_APP_ENV_ID}`;
}

function getAuthHeaders(): HeadersInit {
  return {
    Authorization: `Bearer ${process.env.REACT_APP_CONTENTFUL_DELIVERY_TOKEN}`,
  };
}

async function contentfulGetJson<T>(path: string, params: Record<string, string>): Promise<T> {
  const url = new URL(`${getContentfulBaseUrl()}${path}`);
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });
  const response = await fetch(url.toString(), { headers: getAuthHeaders() });
  if (!response.ok) {
    throw new Error(`Contentful request failed: ${response.status} ${response.statusText}`);
  }
  return response.json() as Promise<T>;
}

function transformProjectsResponse(res: EntryCollection<IBlogPostFields>): ThumbNail[] {
  const assets: Asset[] = res?.includes?.Asset ?? [];
  const resources = assets.reduce<Record<string, string>>((accumulator, asset: Asset) => ({
    ...accumulator,
    [asset.sys.id]: asset.fields.file.url,
  }), {});

  return res.items.map(({ sys: { id }, fields: { slug, title, thumbnail } }) => ({
    id,
    title,
    slug,
    src: resources[thumbnail.sys.id],
  }));
}

function transformProjectResponse(res: EntryCollection<IBlogPostFields>): IBlogPostTransformed {
  const resources = getResourcesFromAsset(res.includes.Asset);
  const item = res.items[0];

  return {
    id: item.sys.id,
    resources,
    fields: Object.keys(item.fields).reduce<TransformedProjectFields>((accumulator, key) => {
      const keyName = key as keyof typeof item.fields;

      if (['thumbnail', 'pcCapture', 'spCapture'].includes(keyName)) {
        return {
          ...accumulator,
          [keyName]: getIdForMediaField(item.fields[keyName] as Asset),
        };
      }

      if (keyName === 'captures') {
        return {
          ...accumulator,
          [keyName]: (item.fields[keyName] as Asset[]).map(getIdForMediaField),
        };
      }

      return {
        ...accumulator,
        [keyName]: item.fields[keyName],
      };
    }, {} as TransformedProjectFields),
  };
}

export async function fetchAbout(): Promise<EntryCollection<IAboutFields>> {
  return contentfulGetJson<EntryCollection<IAboutFields>>('/entries', {
    content_type: 'about',
  });
}

export async function fetchExperience(): Promise<EntryCollection<IExperienceFields>> {
  return contentfulGetJson<EntryCollection<IExperienceFields>>('/entries', {
    content_type: 'experience',
    order: '-fields.date_start',
  });
}

export async function fetchProjects(): Promise<ThumbNail[]> {
  const res = await contentfulGetJson<EntryCollection<IBlogPostFields>>('/entries', {
    select: 'sys.id,fields.title,fields.slug,fields.thumbnail',
    content_type: 'blogPost',
  });
  return transformProjectsResponse(res);
}

export async function fetchProject(slug: string): Promise<IBlogPostTransformed> {
  const res = await contentfulGetJson<EntryCollection<IBlogPostFields>>('/entries', {
    'fields.slug': slug,
    content_type: 'blogPost',
  });
  return transformProjectResponse(res);
}

function getResourcesFromAsset(assets: Asset[]): AssetResources {
  return assets.reduce(
    (accumulator, asset) => ({
      ...accumulator,
      [asset.sys.id]: asset.fields.file.url,
    }),
    {}
  );
}

function getIdForMediaField(media: Asset) {
  return media.sys.id;
}
